import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePremiumBotStats } from '@/hooks/use-premium-bot-stats';
import { formatCurrency } from '@/utils/formatters';
import { Bot, Users, DollarSign, TrendingUp, Award, RotateCcw } from 'lucide-react';

export const PremiumBotStatsCards = () => {
  const { data: stats, isLoading, error } = usePremiumBotStats();

  if (error) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-sm text-destructive">Failed to load stats</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Premium Bots",
      value: stats?.totalPremiumBots || 0,
      description: `${stats?.activePremiumBots || 0} active`,
      icon: Bot,
      trend: null
    },
    {
      title: "Connected Accounts", 
      value: stats?.totalConnectedAccounts || 0,
      description: `${stats?.activeConnectedAccounts || 0} active`,
      icon: Users,
      trend: null
    },
    {
      title: "Total Capital",
      value: formatCurrency(stats?.totalCapital || 0),
      description: "Managed capital",
      icon: DollarSign,
      trend: null
    },
    {
      title: "Reverse Positions",
      value: stats?.reversePositionAccounts || 0,
      description: "Shadow bot accounts",
      icon: RotateCcw,
      trend: null
    },
    {
      title: "Growth Rate",
      value: `${stats?.growthRate || 0}%`,
      description: "30-day growth",
      icon: TrendingUp,
      trend: stats?.growthRate ? (stats.growthRate > 0 ? 'up' : stats.growthRate < 0 ? 'down' : 'neutral') : 'neutral'
    },
    {
      title: "Top Performer",
      value: stats?.topPerformingBot?.accountCount || 0,
      description: stats?.topPerformingBot?.name || 'No data',
      icon: Award,
      trend: null
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
      {statCards.map((stat, index) => (
        <Card key={index}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {stat.title}
            </CardTitle>
            <stat.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-4 w-24" />
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold flex items-center gap-2">
                  {stat.value}
                  {stat.trend === 'up' && (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  )}
                  {stat.trend === 'down' && (
                    <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stat.description}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};